import { useState, useCallback } from "react";

import GoogleMap from "components/googlemap/GoogleMap";

export type NormalizedAddress = {
  /** 都道府県（例：東京都、大阪府） */
  province: string;
  /** 市区町村（例：港区、新宿区） */
  district: string;
  /** 番地（例：三田5-4-27） */
  street: string;
};

/**
 * 住所正規化を行うカスタムフック
 *
 * Google Maps Geocoding APIを使用して住所を都道府県、市区町村、番地に正規化します。
 * 処理中の重複実行を防ぐため、既に処理中の場合は何も実行しません。
 *
 * @returns 住所正規化に関する機能と状態を提供するオブジェクト
 * @returns {Function} returns.normalizeAddress - 住所を正規化する関数
 * @returns {boolean} returns.isLoading - 正規化処理中の状態
 * @returns {string|null} returns.error - エラーメッセージ（エラーがない場合はnull）
 * @returns {Function} returns.TemporaryHiddenGoogleMap - Google Maps API読み込み用の一時的なコンポーネント
 *
 * @example
 * ```typescript
 * const { normalizeAddress, isLoading, error, TemporaryHiddenGoogleMap } = useAddressNormalization();
 *
 * // 住所を正規化
 * const result = await normalizeAddress("東京都港区三田5-4-27");
 * if (result) {
 *   console.log(result.province); // "東京都"
 *   console.log(result.district); // "港区"
 *   console.log(result.street);   // "三田5-4-27"
 * }
 * ```
 */
export const useAddressNormalization = () => {
  const [showGoogleMapForGeocoding, setShowGoogleMapForGeocoding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Google Maps APIが読み込まれるまで待機する関数
   *
   * @returns Google Maps APIが読み込まれた際にresolveされるPromise
   */
  const waitForGoogleMaps = (): Promise<void> => {
    return new Promise((resolve) => {
      const checkGoogleMaps = () => {
        if (typeof window !== "undefined" && window.google && window.google.maps) {
          resolve();
        } else {
          setTimeout(checkGoogleMaps, 100);
        }
      };
      checkGoogleMaps();
    });
  };

  /**
   * 住所を正規化する関数
   *
   * Google Maps Geocoding APIを使用して住所を都道府県、市区町村、番地に分割します。
   * 既に処理中の場合は何も実行せず、nullを返します。
   *
   * @param address - 正規化する住所文字列
   * @returns 正規化された住所情報、または正規化に失敗した場合はnull
   *
   * @example
   * ```typescript
   * const result = await normalizeAddress("東京都港区三田5-4-27");
   * if (result) {
   *   console.log(result.province); // "東京都"
   *   console.log(result.district); // "港区"
   *   console.log(result.street);   // "三田5-4-27"
   *   }
   * ```
   */
  const normalizeAddress = useCallback(
    async (address: string): Promise<NormalizedAddress | null> => {
      if (!address.trim()) return null;

      // 既に処理中の場合は何もしない
      if (isLoading) {
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        setShowGoogleMapForGeocoding(true);
        await waitForGoogleMaps();

        const geocoder = new google.maps.Geocoder();
        const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK" && results) {
              resolve(results);
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          });
        });

        if (result.length > 0) {
          const addressComponents = result[0].address_components;
          let normalizedProvince = "";
          let normalizedDistrict = "";

          for (const component of addressComponents) {
            const types = component.types;

            if (types.includes("administrative_area_level_1")) {
              normalizedProvince = component.long_name;
            } else if (types.includes("locality")) {
              normalizedDistrict = component.long_name;
            }
          }

          let normalizedStreet = address;

          if (normalizedProvince) {
            normalizedStreet = normalizedStreet.replace(new RegExp(normalizedProvince, "g"), "").trim();
          }

          if (normalizedDistrict) {
            normalizedStreet = normalizedStreet.replace(new RegExp(normalizedDistrict, "g"), "").trim();
          }

          const normalizedResult = {
            province: normalizedProvince,
            district: normalizedDistrict,
            street: normalizedStreet,
          };

          return normalizedResult;
        }

        return null;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "住所の正規化に失敗しました";
        setError(errorMessage);
        console.error("住所の正規化に失敗しました:", error);

        return null;
      } finally {
        setIsLoading(false);
        setShowGoogleMapForGeocoding(false);
      }
    },
    [isLoading] // isLoadingのみを依存配列に含める
  );

  /**
   * Google Maps API読み込み用の一時的なコンポーネント
   *
   * 住所正規化処理中に画面外に配置され、Google Maps APIの読み込みを確実にします。
   * 処理完了後は自動的に非表示になります。
   *
   * @returns Google Maps API読み込み用のJSX要素、または読み込み不要の場合はnull
   */
  const TemporaryHiddenGoogleMap = () => {
    if (!showGoogleMapForGeocoding) return null;

    return (
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px", width: "1px", height: "1px" }}>
        <GoogleMap>
          <div />
        </GoogleMap>
      </div>
    );
  };

  return {
    normalizeAddress,
    isLoading,
    error,
    TemporaryHiddenGoogleMap,
  };
};
